import React, { useState } from 'react';
import {
    X,
    Save,
    Calendar,
    Tag,
    User,
    FileText,
    Flag,
    Plus,
    Trash2
} from 'lucide-react';

const CreateTaskModal = ({ show, columnId, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        startDate: '',
        tags: [],
        columnId: columnId
    });
    const [newTag, setNewTag] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    React.useEffect(() => {
        if (columnId) {
            setFormData(prev => ({ ...prev, columnId }));
        }
    }, [columnId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Task title is required';
        }

        if (formData.startDate && formData.dueDate) {
            const startDate = new Date(formData.startDate);
            const dueDate = new Date(formData.dueDate);

            if (startDate >= dueDate) {
                newErrors.dueDate = 'Due date must be after start date';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const newTask = {
                ...formData,
                _id: Date.now().toString(),
                createdAt: new Date().toISOString(),
                order: 0
            };

            await onSubmit(newTask);
            
            // Reset form
            setFormData({
                title: '',
                description: '',
                priority: 'medium',
                dueDate: '',
                startDate: '',
                tags: [],
                columnId: columnId
            });
            setErrors({});
        } catch (error) {
            setErrors({ submit: error.message || 'An error occurred' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setErrors({});
        setNewTag('');
        setFormData({
            title: '',
            description: '',
            priority: 'medium',
            dueDate: '',
            startDate: '',
            tags: [],
            columnId: columnId
        });
        onClose();
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-container">
                <div className="modal-header">
                    <h2 className="modal-title">
                        <Plus style={{ width: '20px', height: '20px' }} />
                        Create New Task
                    </h2>
                    <button
                        className="modal-close"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <X style={{ width: '20px', height: '20px' }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="modal-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="title" className="form-label">
                                <FileText style={{ width: '16px', height: '16px' }} />
                                Task Title *
                            </label>
                            <input
                                id="title"
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                className={`form-input ${errors.title ? 'error' : ''}`}
                                placeholder="Enter task title..."
                                disabled={isSubmitting}
                            />
                            {errors.title && <span className="error-text">{errors.title}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="priority" className="form-label">
                                <Flag style={{ width: '16px', height: '16px' }} />
                                Priority
                            </label>
                            <select
                                id="priority"
                                name="priority"
                                value={formData.priority}
                                onChange={handleInputChange}
                                className="form-select"
                                disabled={isSubmitting}
                            >
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="description" className="form-label">
                            <FileText style={{ width: '16px', height: '16px' }} />
                            Description
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className="form-textarea"
                            placeholder="Describe the task..."
                            rows="4"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="startDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Start Date
                            </label>
                            <input
                                id="startDate"
                                type="date"
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                className="form-input"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="dueDate" className="form-label">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Due Date
                            </label>
                            <input
                                id="dueDate"
                                type="date"
                                name="dueDate"
                                value={formData.dueDate}
                                onChange={handleInputChange}
                                className={`form-input ${errors.dueDate ? 'error' : ''}`}
                                disabled={isSubmitting}
                            />
                            {errors.dueDate && <span className="error-text">{errors.dueDate}</span>}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            <Tag style={{ width: '16px', height: '16px' }} />
                            Tags
                        </label>
                        <div className="tags-input-container">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddTag();
                                    }
                                }}
                                className="form-input"
                                placeholder="Add a tag..."
                                disabled={isSubmitting}
                            />
                            <button
                                type="button"
                                onClick={handleAddTag}
                                className="add-tag-button"
                                disabled={!newTag.trim() || isSubmitting}
                            >
                                <Plus style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>

                        {formData.tags.length > 0 && (
                            <div className="tags-display">
                                {formData.tags.map((tag, index) => (
                                    <span key={index} className="tag-item">
                                        {tag}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(tag)}
                                            className="remove-tag-button"
                                            disabled={isSubmitting}
                                        >
                                            <Trash2 style={{ width: '12px', height: '12px' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {errors.submit && (
                        <div className="error-message">
                            {errors.submit}
                        </div>
                    )}

                    <div className="modal-footer">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="button secondary"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="button primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="spinner"></div>
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Save style={{ width: '16px', height: '16px' }} />
                                    Create Task
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateTaskModal;